import { DndContext, MouseSensor, TouchSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { SortableContext, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";

import { SortableQuestion } from "modules/ChecklistsDashboard/components/EditChecklistModal/components/SortableQuestions/components";

import type { DragEndEvent } from "@dnd-kit/core";
import type { Question } from "models";

type SortableQuestionsProps = {
  questions: Question[];
  canEdit: boolean;
  onChange: (questions: Question[]) => void;
};

const getSortableId = (question: Question) => `question-${question.ordering}`;

const reindexQuestions = (questions: Question[]) => questions.map((question, questionIdx) => ({ ...question, ordering: questionIdx }));

export const SortableQuestions = ({ questions, canEdit, onChange }: SortableQuestionsProps) => {
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 100 } })
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;

    const activeIdx = questions.findIndex(question => getSortableId(question) === active.id);
    const overIdx = questions.findIndex(question => getSortableId(question) === over.id);
    if (activeIdx === -1 || overIdx === -1) return;

    onChange(reindexQuestions(arrayMove(questions, activeIdx, overIdx)));
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} modifiers={[restrictToVerticalAxis]} onDragEnd={handleDragEnd}>
      <SortableContext items={questions.map(getSortableId)} strategy={verticalListSortingStrategy} disabled={!canEdit}>
        {questions.map((question, questionIdx) => (
          <SortableQuestion
            key={getSortableId(question)}
            sortableId={getSortableId(question)}
            question={question}
            canEdit={canEdit}
            onRemove={() => onChange(reindexQuestions(questions.toSpliced(questionIdx, 1)))}
          />
        ))}
      </SortableContext>
    </DndContext>
  );
};
